/**
 * useDialogState - Helper hook for managing dialog open/close state via Zustand
 *
 * Provides a simple boolean state interface while using centralized Zustand storage.
 * This allows gradual migration from local useState to centralized state management.
 *
 * @example
 * // Before:
 * const [editOpen, setEditOpen] = useState(false);
 *
 * // After:
 * const [editOpen, setEditOpen] = useDialogState("editCompany");
 */

import { useCallback } from 'react';
import { useUIStore } from '@/stores/ui-store';

/**
 * Hook that provides open/close state for a local dialog
 * Replaces useState declarations while using centralized Zustand storage
 */
export function useDialogState(key: string): [boolean, (open: boolean) => void] {
  const isOpen = useUIStore((s) => s.localDialogs[key] ?? false);
  const setLocalDialog = useUIStore((s) => s.setLocalDialog);

  const setOpen = useCallback(
    (open: boolean) => {
      setLocalDialog(key, open);
    },
    [key, setLocalDialog]
  );

  return [isOpen, setOpen];
}
